import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  GetStaffManagementResponseType,
  GetSupplyOrderListResponseType,
  GetSupplyOrderRemarksResponseType,
} from 'features/SupplyOrder/services/outboundSO'
import { GetAllLocationsResponseType } from 'features/Location/services/location'
import { RouteWaveListItemType } from 'features/Midmile/service/routeAndSchedule'
import { MasterAreaPickingDataType } from 'features/MasterAreaPicking/services/masterAreaPicking'
import {
  SLICE_NAME,
  fetchGetAllLocation,
  fetchGetSupplyOrder,
  fetchGetSupplyOrderRemarks,
  fetchGetRouteMaster,
  fetchGetPickingArea,
  fetchPostCopySupplyOrder,
  fetchPutSupplyOrderStatus,
  fetchGetFeatureFlags,
  fetchGetPicker,
  fetchGetSupplyOrderGrandData,
  fetchPostAssignPicker,
  fetchGetSupplyOrderGrandKoliLabel,
  fetchGetSupplyOrderDeliveryDocument,
  fetchGetSupplyOrderDeliveryDocumentLoading,
  fetchPostCreateGroupSO,
  fetchPutCancelGroupSO,
  fetchPutStartSupplyOrderLoading,
  fetchGetPrintSupplyOrderGroup,
  fetchGetSupplyOrderDeliveryDocumentLoadingDetail,
  fetchPostBulkAssignPicker,
} from './thunk'

type OutboundSOSliceType = {
  isLoading: boolean
  checkedIds: number[]
  selectedItems: Record<number, GetSupplyOrderListResponseType['data'][number]>
  data: GetSupplyOrderListResponseType['data']
  constants: {
    remarks: GetSupplyOrderRemarksResponseType['data']
    locations: GetAllLocationsResponseType
    routesMaster: RouteWaveListItemType[]
    pickingAreas: MasterAreaPickingDataType[]
  }
  popup: {
    filter: {
      isOpen: boolean
    }
    assignPicker: {
      isOpen: boolean
      type?: 'ASSIGN_PICKER' | 'REASSIGN_PICKER' | 'ASSIGN_BULK_SO'
      item: Nullable<{
        supplyOrderId?: number
        locationId: number
      }>
      pickerList: GetStaffManagementResponseType['data']
    }
    checkerPacker: {
      isOpen: boolean
      isFlaggingPackingRecordingActive?: boolean
      item: Nullable<{
        supplyOrderId: number
        locationId: number
      }>
    }
  }
}

const initialState: OutboundSOSliceType = {
  isLoading: false,
  checkedIds: [],
  selectedItems: {},
  data: [],
  constants: {
    remarks: [],
    locations: [],
    routesMaster: [],
    pickingAreas: [],
  },
  popup: {
    filter: {
      isOpen: false,
    },
    assignPicker: {
      isOpen: false,
      item: null,
      pickerList: [],
    },
    checkerPacker: {
      isOpen: false,
      item: null,
    },
  },
}

const outboundSOSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    resetChecked: (state) => {
      state.checkedIds = []
    },
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload
    },
    setOpenFilterPopup: (state, { payload }: PayloadAction<boolean>) => {
      state.popup.filter.isOpen = payload
    },
    setOpenAssignPickerPopup: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean
        type?: 'ASSIGN_PICKER' | 'REASSIGN_PICKER' | 'ASSIGN_BULK_SO'
        item?: {
          supplyOrderId?: number
          locationId: number
        }
      }>,
    ) => {
      const { isOpen, item, type } = payload

      state.popup.assignPicker.isOpen = isOpen
      state.popup.assignPicker.type = type
      state.popup.assignPicker.item = item ?? null
    },
    setOpenCheckerPackerPopup: (
      state,
      {
        payload,
      }: PayloadAction<{
        isOpen: boolean
        isFlaggingPackingRecordingActive?: boolean
        item?: {
          supplyOrderId: number
          locationId: number
        }
      }>,
    ) => {
      const { isOpen, isFlaggingPackingRecordingActive, item } = payload

      state.popup.checkerPacker.isOpen = isOpen
      state.popup.checkerPacker.isFlaggingPackingRecordingActive =
        isFlaggingPackingRecordingActive ?? false
      state.popup.checkerPacker.item = item ?? null
    },
    setCheckItem: (state, { payload }: PayloadAction<number>) => {
      const index = state.checkedIds.indexOf(payload)

      if (index >= 0) {
        state.checkedIds.splice(index, 1)
        delete state.selectedItems[payload]
      } else {
        state.checkedIds.push(payload)
        const selectedItem = state.data.find((item) => item.id === payload)
        if (selectedItem) {
          state.selectedItems[payload] = selectedItem
        }
      }
    },
    setCheckAll: (state, { payload }: PayloadAction<number[]>) => {
      state.checkedIds = Array.from(new Set([...state.checkedIds, ...payload]))
      state.selectedItems = {
        ...state.selectedItems,
        ...payload.reduce((acc, id) => {
          const selectedItem = state.data.find((item) => item.id === id)
          if (selectedItem) {
            return { ...acc, [id]: selectedItem }
          }
          return acc
        }, {} as Record<number, GetSupplyOrderListResponseType['data'][number]>),
      }
    },
    setUncheckAll: (state, { payload }: PayloadAction<number[]>) => {
      state.checkedIds = state.checkedIds.filter((id) => !payload.includes(id))
      state.selectedItems = Object.fromEntries(
        Object.entries(state.selectedItems).filter(([key]) => !payload.includes(Number(key))),
      )
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<OutboundSOSliceType>) => {
      builder
        .addCase(fetchGetSupplyOrder.fulfilled, (state, { payload }) => {
          state.data = payload.data
        })
        .addCase(fetchGetSupplyOrderRemarks.fulfilled, (state, { payload }) => {
          state.constants.remarks = payload
        })
        .addCase(fetchGetAllLocation.fulfilled, (state, { payload }) => {
          state.constants.locations = payload
        })
        .addCase(fetchGetRouteMaster.fulfilled, (state, { payload }) => {
          state.constants.routesMaster = payload
        })
        .addCase(fetchGetPickingArea.fulfilled, (state, { payload }) => {
          state.constants.pickingAreas = payload
        })
        .addCase(fetchGetPicker.fulfilled, (state, { payload }) => {
          state.popup.assignPicker.pickerList = payload
        })
        .addCase(fetchPostBulkAssignPicker.fulfilled, (state) => {
          state.checkedIds = []
          state.selectedItems = {}
        })
    },
    [
      fetchGetSupplyOrder,
      fetchGetFeatureFlags,
      fetchGetPicker,
      fetchGetSupplyOrderGrandData,
      fetchGetSupplyOrderGrandKoliLabel,
      fetchGetSupplyOrderDeliveryDocument,
      fetchGetSupplyOrderDeliveryDocumentLoading,
      fetchGetSupplyOrderDeliveryDocumentLoadingDetail,
      fetchGetPrintSupplyOrderGroup,
      fetchPostAssignPicker,
      fetchPostBulkAssignPicker,
      fetchPostCopySupplyOrder,
      fetchPutSupplyOrderStatus,
      fetchPostCreateGroupSO,
      fetchPutCancelGroupSO,
      fetchPutStartSupplyOrderLoading,
    ],
  ),
})

export const {
  reset,
  resetChecked,
  setIsLoading,
  setOpenFilterPopup,
  setOpenAssignPickerPopup,
  setOpenCheckerPackerPopup,
  setCheckItem,
  setCheckAll,
  setUncheckAll,
} = outboundSOSlice.actions
export default outboundSOSlice.reducer
